import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { EmailSignIn } from '@/components/EmailSignIn';
import { LoginButton } from '@/components/LoginButton';

import { useAuth } from '@/hooks/useAuth';

import { isProduction } from '@/utils/apiClient';
import { isSafeRelativePath } from '@/utils/return-url';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { error, signIn } = useAuth();
  const [showTestLogin, setShowTestLogin] = useState(false);

  // Return user to where they came from (state.from, or current path when signed-out App.tsx renders <Login /> at "*"); skip `/` and `/login` to avoid 404/loop.
  const NON_RETURN_PATHS = new Set(['/', '/login']);
  const fromState = (location.state as { from?: unknown } | null)?.from;
  const currentPath = `${location.pathname}${location.search}${location.hash}`;
  const candidate =
    fromState ?? (NON_RETURN_PATHS.has(location.pathname) ? undefined : currentPath);
  const returnUrl = isSafeRelativePath(candidate) ? candidate : undefined;
  const postLoginPath = returnUrl ?? '/dashboard';

  return (
    <div className="flex min-h-screen items-center justify-center bg-warm-gray-50 px-4">
      <div className="w-full max-w-md text-center">
        <h1 className="mb-2 text-4xl font-bold text-gray-900">Unleash your potential.</h1>

        <div className="mb-12">
          <span className="text-lg font-bold text-gray-700">Powered by </span>
          <span className="text-xl font-bold text-red-600">X-Team</span>
        </div>

        {!showTestLogin ? (
          <>
            <div className="mb-8">
              <LoginButton onClick={() => signIn(returnUrl)} />

              {error && (
                <div
                  data-testid="login-error"
                  className="my-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-600"
                >
                  {error}
                </div>
              )}
            </div>
            {!isProduction && (
              <div className="mb-6">
                <button
                  onClick={() => setShowTestLogin(true)}
                  className="text-sm text-red-600 underline transition-colors hover:text-red-700"
                >
                  Log in using test user?
                </button>
              </div>
            )}
            <p className="text-sm text-warm-gray-400">
              If your Google account isn&apos;t setup, please reach out to Support via the{' '}
              <a
                href="https://x-team-internal.atlassian.net/servicedesk/customer/portal/2"
                className="text-red-600 transition-colors hover:text-red-700"
                target="_blank"
                rel="noopener noreferrer"
              >
                Help Desk
              </a>
              .
            </p>
          </>
        ) : (
          <div className="space-y-6">
            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4">
              <p className="mb-3 text-sm font-medium text-yellow-800">Test User Sign-In</p>
              {!isProduction && (
                <EmailSignIn onSuccess={() => navigate(postLoginPath, { replace: true })} />
              )}

              {error && (
                <div
                  data-testid="login-error"
                  className="my-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-600"
                >
                  {error}
                </div>
              )}
            </div>

            <button
              onClick={() => setShowTestLogin(false)}
              className="text-sm text-gray-600 underline transition-colors hover:text-gray-800"
            >
              Back to Google Sign-In
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export { Login };
